/**
 * Post-deposit balance sync: the decision half.
 *
 * `DepositModal` fires `onSuccess` the moment Stripe confirms the charge, but the owner's MAIN
 * wallet is credited server-side (webhook), so a single refetch right then usually reads the OLD
 * balance and the UI looks like nothing happened. That is the worst possible moment to go quiet:
 * the owner has just handed over money.
 *
 * So we re-read `/wallet/list` until the balance actually moves, up to
 * {@link DEPOSIT_POLL_MAX_ATTEMPTS} attempts, and surface every phase (checking · landed · taking
 * too long) rather than failing silent.
 *
 * Pure, with no React and no store import, so the schedule and the stop conditions are unit-tested
 * without a render. The React shell lives in `./useDepositSync`.
 */

/** Give up after this many reads. At the cadence below that is roughly 45 seconds. */
export const DEPOSIT_POLL_MAX_ATTEMPTS = 20;

/**
 * Poll cadence. The first read fires immediately so the UI reacts the instant the charge
 * confirms, then it eases off: most webhooks land in the first few seconds, and the later,
 * slower attempts are there to cover a straggler without hammering the API for a full minute.
 */
export function depositPollDelayMs(attempt: number): number {
  if (attempt <= 0) return 0;
  if (attempt < 5) return 1000;
  if (attempt < 10) return 2000;
  return 3000;
}

export type DepositPollDecision =
  /** The balance moved: `credited` is how much appeared, in major units. */
  | { type: 'landed'; credited: number }
  /** Every attempt ran and the balance never moved. */
  | { type: 'exhausted' }
  /** Read again in `delayMs`. */
  | { type: 'retry'; delayMs: number };

export type DepositPollInput = {
  /** The balance as it stood BEFORE the deposit, major units. */
  baseline: number;
  /** The balance as it reads now, or `null` while a read is in flight. */
  balance: number | null;
  /** How many reads have already been issued. */
  attempt: number;
  maxAttempts?: number;
};

/** Compare in whole cents so float drift can't read as a credit. */
const cents = (value: number): number => Math.round(value * 100);

export function decideDepositPoll({
  baseline,
  balance,
  attempt,
  maxAttempts = DEPOSIT_POLL_MAX_ATTEMPTS,
}: DepositPollInput): DepositPollDecision {
  // `balance` is null while the request is in flight (the slice drops `loaded`), which is not
  // evidence of anything. Only a strictly higher balance counts as the deposit landing.
  if (balance !== null && Number.isFinite(balance) && cents(balance) > cents(baseline)) {
    return { type: 'landed', credited: (cents(balance) - cents(baseline)) / 100 };
  }
  if (attempt >= maxAttempts) return { type: 'exhausted' };
  return { type: 'retry', delayMs: depositPollDelayMs(attempt) };
}
